import { UserClaim } from '@treasury/domain/company/user';
import { TmBaseComponent } from '@treasury/presentation';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import {
    ClaimGroup,
    getClaimDisplayLabel,
    groupClaimsByBaseName,
} from '../utils/claim-grouping.util';

type FormControlEvent<T> = CustomEvent<{ value: T; isValid: boolean }>;

@customElement('tm-company-user-product-entitlements')
export class CompanyUserProductEntitlements extends TmBaseComponent {
    @property()
    public entitlements: UserClaim[] = [];

    public render() {
        if (this.entitlements.length < 1) {
            return nothing;
        }
        return html`<div class="text-md font-bold">Set user entitlements</div>
            <div class="text-sm text-gray-500 mb-5">
                Users entitlements apply to all accounts and have no account overrides.
            </div>
            ${this.renderProductEntitlements()}`;
    }

    private renderProductEntitlements() {
        const groupedClaims = groupClaimsByBaseName(this.entitlements);
        return html`<div class="flex flex-row gap-x-32">
            ${groupedClaims.map(group => this.renderClaimGroup(group))}
        </div>`;
    }

    private renderClaimGroup(group: ClaimGroup<UserClaim>) {
        const isAllSelected = group.claims.every(child => child.claim.hasUserClaim);
        return html`<div>
            <tm-wc-checkbox
                class="ext-lg font-semibold mb-3"
                @value-changed=${(e: FormControlEvent<boolean>) => this.handleSelectAll(e, group)}
                .initial=${isAllSelected}
                .label=${group.baseName}
            ></tm-wc-checkbox>
            <div class="flex flex-col gap-2">
                ${group.claims.map(groupedClaim =>
                    this.renderClaim(groupedClaim.claim, groupedClaim.action)
                )}
            </div>
        </div>`;
    }

    private renderClaim(claim: UserClaim, action?: string) {
        const label = getClaimDisplayLabel(claim, action);
        return html`<div class="min-w-0 flex-shrink-0">
            <tm-wc-checkbox
                class="ml-10"
                .label=${label}
                .initial=${claim.hasUserClaim}
                @value-changed=${(e: FormControlEvent<boolean>) => this.handleClaimChange(e, claim)}
            ></tm-wc-checkbox>
        </div>`;
    }

    private handleClaimChange(e: FormControlEvent<boolean>, claim: UserClaim) {
        const { value } = e.detail;
        claim.hasUserClaim = value;
        this.requestUpdate();
    }

    private handleSelectAll(e: FormControlEvent<boolean>, group: ClaimGroup<UserClaim>) {
        const { value } = e.detail;
        group.claims.forEach(child => {
            child.claim.hasUserClaim = value;
        });
        this.requestUpdate();
    }
}
